import {
  SurfaceBody,
  SurfaceCloseButton,
  SurfaceFooter,
  SurfaceHeader,
  type SurfaceHeaderProps,
} from "@ui/components/SheetChrome";
import { SheetSurface, type SheetSurfaceRootProps, useSheetSurface } from "@ui/components/SheetSurface";
import type { SheetTrack } from "@ui/primitives/Sheet";
import { createContext, type ReactNode, use } from "react";
import { PAGE_STACK_SHEET_ID } from "./PageStack";
import { pageBackdrop, pagePanel } from "./page.styles";

export interface PageRootProps extends Omit<SheetSurfaceRootProps, "grabber" | "role" | "stack"> {
  /**
   * Mobile travel axis. "bottom" (default): page-from-bottom, for modal
   * flows isOpen over the current context. "right": push navigation —
   * the page slides in from the right with iOS-style swipe-back.
   */
  track?: Extract<SheetTrack, "bottom" | "right">;
  /**
   * Wires this Page to the {@link PageStackRoot} outlet so the underlying
   * page parallaxes behind it while it travels. Implies push semantics —
   * pair with `track="right"`.
   */
  pushNavigation?: boolean;
}

const PageTrackContext = createContext<"bottom" | "right">("bottom");

/**
 * Fullscreen flow surface: a page that travels in over the current context.
 * Mobile: covers the viewport (page-from-bottom, or push-from-right with
 * swipe-back via `track="right"`). Desktop: a detached xl-width card.
 *
 * Shares the chrome parts with `Dialog` — `Page.Header`/`Body`/`Footer` are
 * the same components, so flows read identically in both surfaces.
 *
 * ```tsx
 * <Page.Root isOpen={open} onOpenChange={setOpen}>
 *   <Page.Content>
 *     <Page.Header title={t("…")} />
 *     <Page.Body>…</Page.Body>
 *   </Page.Content>
 * </Page.Root>
 * ```
 */
function PageRoot({ children, track = "bottom", pushNavigation = false, size = "xl", ...props }: PageRootProps) {
  return (
    <SheetSurface.Root {...props} stack={pushNavigation ? PAGE_STACK_SHEET_ID : undefined} size={size}>
      <PageTrackContext value={track}>{children}</PageTrackContext>
    </SheetSurface.Root>
  );
}

export interface PageContentProps {
  children: ReactNode;
  className?: string;
}

/** The Page's configuration of the surface: fullscreen on mobile, card on desktop. */
function PageContent({ children, className }: PageContentProps) {
  const { platform, chrome, size } = useSheetSurface();
  const track = use(PageTrackContext);
  const desktop = platform === "desktop";

  return (
    <SheetSurface.View
      backdropClassName={pageBackdrop({ platform })}
      className={pagePanel({ chrome, class: className, platform, size })}
      fill={!desktop}
      placement={desktop ? "center" : track}
      swipe={!desktop && track === "right"}
      track={desktop ? "bottom" : track}
    >
      {children}
    </SheetSurface.View>
  );
}

/** The Page's header treatment: a fullscreen top bar on mobile. */
function PageHeader(props: Omit<SurfaceHeaderProps, "layout">) {
  return <SurfaceHeader {...props} layout="page" />;
}

export const Page = {
  Body: SurfaceBody,
  CloseButton: SurfaceCloseButton,
  Content: PageContent,
  Footer: SurfaceFooter,
  Header: PageHeader,
  Root: PageRoot,
};
